import { DamageType } from "../damageType";
import type { Direction } from "../direction";
import { ALL_DIRECTIONS, directionDelta } from "../direction";
import { BigExplosion } from "../effects/bigExplosion";
import type { ParticleEmitter } from "../interfaces";
import { MovementEffect } from "../effects/movementEffect";
import { ParticleEffect } from "../effects/particleEffect";
import { ParticleType } from "../particleType";
import type { PhysicalThingWithHp } from "../physicalThingWithHp";
import { STD_BASE_HP } from "../physicalThingWithHp";
import { deserializeXY } from "../serialization";
import { SimpleProjectile } from "../simpleProjectile";
import { Sound } from "../things/sounds/sound";
import { SoundId } from "../things/sounds/soundId";
import { Shrapnel } from "./shrapnel";

const SELF_DAMAGE = STD_BASE_HP; // half the direct-target damage

export class Missile extends SimpleProjectile implements ParticleEmitter {
	static key = "Missile";

	getParticleType(): ParticleType {
		return ParticleType.MISSILE_EXHAUST;
	}

	protected damageTarget(target: PhysicalThingWithHp): number {
		return target.applyDamage(DamageType.EXPLOSION, STD_BASE_HP * 2, this);
	}

	onHit(damageDealt: number): void {
		if (!this.active) return;
		this.board!.addNonPhysicalThing(new Sound(SoundId.BIG_EXPLOSION));
		this.board!.addEffect(new BigExplosion(this.xy));
		this.board!.addEffect(new ParticleEffect(this.xy, ParticleType.LARGE_EXPLOSION, 34));
		this.board!.removeAtEndOfTurn(this);

		const player = this.board!.player;
		if (player) {
			if (this.xy.isInSquareRadius(player.xy, 1)) {
				player.applyDamage(DamageType.EXPLOSION, SELF_DAMAGE, this);
			}
		}

		if (damageDealt > 0) {
			for (const dir of ALL_DIRECTIONS) {
				this.createShrapnel(dir);
			}
		}
	}

	private createShrapnel(direction: Direction): void {
		const shrapnelXY = this.xy.add(directionDelta(direction));
		if (this.board!.isInBounds(shrapnelXY)) {
			const shrapnel = new Shrapnel(shrapnelXY, direction);
			this.board!.addThing(shrapnel);
			this.board!.addEffect(new MovementEffect(shrapnel, this.xy, shrapnelXY));
		}
	}

	static deserialize(data: any): Missile {
		const missile = new Missile(deserializeXY(data.xy), data.direction as Direction);
		missile.visualId = data.visualId;
		return missile;
	}
}
